import { create } from 'zustand';
import api from '@/services/api';

/* ─── Types ──────────────────────────────────── */
export interface Notification {
  id: string;
  user_id: string;
  type: string;
  title: string;
  body: string;
  meta?: Record<string, unknown>;
  read_at: string | null;
  created_at: string;
}

interface NotificationState {
  notifications: Notification[];
  unreadCount: number;
  isLoading: boolean;

  fetchNotifications: (unreadOnly?: boolean) => Promise<void>;
  fetchUnreadCount: () => Promise<void>;
  markRead: (id: string) => Promise<void>;
  markAllRead: () => Promise<void>;
  deleteNotification: (id: string) => Promise<void>;
}

export const useNotificationStore = create<NotificationState>((set) => ({
  notifications: [],
  unreadCount: 0,
  isLoading: false,

  fetchNotifications: async (unreadOnly = false) => {
    set({ isLoading: true });
    try {
      const params = unreadOnly ? '?unread=1' : '';
      const { data } = await api.get(`/notifications/${params}`);
      set({
        notifications: data.notifications ?? [],
        unreadCount: data.unread_count ?? 0,
      });
    } catch (err) {
      console.warn('[NotificationStore] fetchNotifications error:', err);
      throw err;
    } finally {
      set({ isLoading: false });
    }
  },

  fetchUnreadCount: async () => {
    try {
      const { data } = await api.get('/notifications/count');
      set({ unreadCount: data.unread_count ?? 0 });
    } catch (err) {
      console.warn('[NotificationStore] fetchUnreadCount error:', err);
    }
  },

  markRead: async (id: string) => {
    try {
      await api.patch(`/notifications/${id}/read`);
      set((s) => ({
        notifications: s.notifications.map((n) =>
          n.id === id ? { ...n, read_at: new Date().toISOString() } : n
        ),
        unreadCount: Math.max(0, s.unreadCount - 1),
      }));
    } catch {
      // silent
    }
  },

  markAllRead: async () => {
    try {
      await api.post('/notifications/read-all');
      set((s) => ({
        notifications: s.notifications.map((n) => ({
          ...n,
          read_at: n.read_at ?? new Date().toISOString(),
        })),
        unreadCount: 0,
      }));
    } catch {
      // silent
    }
  },

  deleteNotification: async (id: string) => {
    try {
      await api.delete(`/notifications/${id}`);
      set((s) => ({
        notifications: s.notifications.filter((n) => n.id !== id),
        unreadCount: s.notifications.find((n) => n.id === id && !n.read_at)
          ? s.unreadCount - 1
          : s.unreadCount,
      }));
    } catch {
      // silent
    }
  },
}));
